import { phasesOf, titleCase } from '@nsb/core';
import { useId, useState } from 'react';
import { errorText } from '../../api.ts';
import { Button, ErrorLine } from '../ui.tsx';
import { endComes, phaseRows, previewAt, previewWords, tickPreview, type Asking, type PhaseGroup, type PhaseRow } from './phaseList.ts';
import { useStepIcons } from './stepIcons.ts';
import { processAction, useTrades, type ProcessModel } from './useProcess.ts';

/**
 * The setup page's list per phase (design study 31, slice 2; Jason's picks q-setup-how C, q-setup-later B, q-r2-setup
 * A), above the drawing: each phase a heading row that opens to its steps of the tree, the phase under way open by
 * itself; a step a row with its picture, its name, whose it is and a tick. Ticked, it is on the job; ticking one shows
 * first what it needs that the job has not got (`TickPreview`), nothing added until Add; unticking files it away, as its
 * filing box does, where it can go. Everything waits in the job's draft until Save the changes. A job kept as it ended
 * (L-19 A) shows where each step is, with no tick.
 */
export function PhaseList({ projectId, model }: { projectId: string; model: ProcessModel }) {
  const icons = useStepIcons();
  // Whose a step is in the organization's names for its trades, as its tile says it.
  const { data: trades } = useTrades();
  const groups = phaseRows(model, icons.urls, (k) => trades?.trades.find((t) => t.key === k)?.name ?? null);
  return (
    <section aria-label="The tree’s steps, by phase" className="mb-8 max-w-3xl">
      {groups.map((g) => (
        <PhaseBlock key={g.key} projectId={projectId} model={model} group={g} pictures={icons.urls} />
      ))}
    </section>
  );
}

function PhaseBlock({ projectId, model, group: g, pictures }: { projectId: string; model: ProcessModel; group: PhaseGroup; pictures: ReadonlyMap<string, string> }) {
  const [open, setOpen] = useState(g.open);
  // What a tick asked for, shown under the row it came from (or the heading, for Tick all) until added or cancelled.
  const [asking, setAsking] = useState<Asking | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const kept = Boolean(model.dto.kept);
  const tickable = g.rows.filter((r) => r.state !== 'on' && !r.repeatable).map((r) => r.key);
  const send = async (path: string, body: unknown): Promise<boolean> => {
    setBusy(true);
    setErr(null);
    try {
      await processAction(projectId, path, body);
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  /** Ticked: added at once when it needs nothing the job lacks and lands on time; else what it brings is shown first. */
  const tick = (keys: string[], all: boolean) => {
    const p = tickPreview(model, keys);
    if (p.adds.every((a) => a.asked) && !p.late) void send('/enable', { keys: p.adds.map((a) => a.key) });
    else setAsking({ keys, all });
  };
  const preview = (at: { phase: string } | { row: string }) =>
    asking && previewAt(asking, at) ? <TickPreview projectId={projectId} model={model} keys={asking.keys} onDone={() => setAsking(null)} onCancel={() => setAsking(null)} /> : null;
  return (
    <div className="border-b border-rule">
      <div className="flex min-h-11 flex-wrap items-baseline gap-x-4 gap-y-1 py-1">
        <button type="button" className="min-h-11 text-left font-display text-[1.375rem] leading-tight hover:underline" aria-expanded={open} onClick={() => setOpen(!open)}>
          {g.name}
        </button>
        <span className="font-mono text-[0.75rem] uppercase tracking-[0.06em] text-ink-3">
          · <span className="num">{g.on}</span> of <span className="num">{g.of}</span> on this job
        </span>
        {!kept && tickable.length ? (
          <span className="font-mono text-[0.75rem] uppercase tracking-[0.06em] text-ink-3">
            ·{' '}
            <button type="button" className="link min-h-11 uppercase text-ink-2 hover:text-ink" disabled={busy} onClick={() => (setOpen(true), tick(tickable, true))}>
              Tick all
            </button>
          </span>
        ) : null}
        <span className="ml-auto font-mono text-[0.75rem] text-ink-3" aria-hidden="true">
          {open ? 'Open' : 'Closed'}
        </span>
      </div>
      {preview({ phase: g.key })}
      {open ? (
        <ul className="pb-3">
          {g.rows.map((r) => (
            <li key={r.key}>
              <Row projectId={projectId} row={r} kept={kept} busy={busy} picture={pictures.get(r.key) ?? null} onTick={() => tick([r.key], false)} onUntick={() => r.stepId && void send(`/steps/${r.stepId}/off`, {})} />
              {preview({ row: r.key })}
            </li>
          ))}
        </ul>
      ) : null}
      <ErrorLine error={err} />
    </div>
  );
}

/** A step of the tree in its phase's list: a tick named by its step, its picture, its name, whose it is, and where it is. */
function Row({ projectId, row: r, kept, busy, picture, onTick, onUntick }: { projectId: string; row: PhaseRow; kept: boolean; busy: boolean; picture: string | null; onTick: () => void; onUntick: () => void }) {
  const name = titleCase(r.title);
  const where = r.state === 'on' ? 'On this job' : r.state === 'left out' ? 'Left out' : 'Not on this job';
  // What a screen reader hears with the tick named by its step: where it is, and why it is held (the review, 2026-10-29).
  const described = useId();
  const body = (
    <>
      {picture ? <img src={picture} alt="" className="h-8 w-11 shrink-0 rounded-[2px] object-cover" /> : <span className="h-8 w-11 shrink-0 rounded-[2px] bg-inset" aria-hidden="true" />}
      <span className="min-w-0 flex-1">
        <span className="block">{name}</span>
        {r.blocked ? (
          <span className="meta block" aria-hidden="true">
            {r.blocked}
          </span>
        ) : null}
      </span>
      <span className="shrink-0 font-mono text-[0.75rem] text-ink-3">{r.who}</span>
      {kept || r.state === 'left out' ? (
        <span className="shrink-0 font-mono text-[0.75rem] text-ink-3" aria-hidden="true">
          {where}
        </span>
      ) : null}
      <span id={described} className="sr-only">
        {where}
        {r.blocked ? `. ${r.blocked}` : ''}
      </span>
    </>
  );
  if (r.repeatable) return <NamedCopy projectId={projectId} row={r} kept={kept} picture={picture} />;
  if (kept) return <div className="flex min-h-11 items-center gap-3 border-t border-rule py-1.5">{body}</div>;
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 border-t border-rule py-1.5">
      <input
        type="checkbox"
        className="size-4 shrink-0 accent-ink"
        aria-label={name}
        aria-describedby={described}
        checked={r.state === 'on'}
        disabled={busy || Boolean(r.blocked)}
        onChange={(e) => (e.target.checked ? onTick() : onUntick())}
      />
      {body}
    </label>
  );
}

/** A step added by name, as many as the job needs (a named copy): its name, and Add. */
function NamedCopy({ projectId, row: r, kept, picture }: { projectId: string; row: PhaseRow; kept: boolean; picture: string | null }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const add = async () => {
    setBusy(true);
    setErr(null);
    try {
      await processAction(projectId, '/copies', { key: r.key, name: name.trim() });
      setName('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-t border-rule py-1.5">
      <div className="flex min-h-11 flex-wrap items-center gap-3">
        {picture ? <img src={picture} alt="" className="h-8 w-11 shrink-0 rounded-[2px] object-cover" /> : <span className="h-8 w-11 shrink-0 rounded-[2px] bg-inset" aria-hidden="true" />}
        <span className="min-w-0 flex-1">
          {titleCase(r.title)} <span className="meta">· added by name{r.state === 'on' ? ', one on this job' : ''}</span>
        </span>
        {kept ? null : (
          <span className="flex items-center gap-2">
            <input className="field w-40 py-1 text-sm" aria-label={`Name a new ${titleCase(r.title)}`} placeholder="Its name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            <Button tone="secondary" busy={busy} disabled={!name.trim()} onClick={() => void add()}>
              Add
            </Button>
          </span>
        )}
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

/**
 * What ticking `keys` brings, before anything is added (q-r2-setup A): the steps it needs that the job has not got, all
 * the way back, each ticked and able to be unticked, in the tree's order with where it lands; one whose phase has closed
 * marked late, the add asking NSB's reason (design study 21, land A), which the client confirms. Add adds the steps asked
 * and the needs left ticked; Only adds the steps asked alone; Cancel adds nothing. Into the job's draft, as every change
 * on the setup page goes.
 */
export function TickPreview({ projectId, model, keys, onDone, onCancel }: { projectId: string; model: ProcessModel; keys: readonly string[]; onDone: () => void; onCancel: () => void }) {
  const icons = useStepIcons();
  const { adds } = tickPreview(model, keys);
  // A need the job left out starts unticked: NSB filed it on purpose (the review, 2026-10-29).
  const [left, setLeft] = useState<ReadonlySet<string>>(() => new Set(adds.filter((a) => !a.asked && a.filed).map((a) => a.key)));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const asked = adds.filter((a) => a.asked);
  const needs = adds.filter((a) => !a.asked);
  const kept = adds.filter((a) => a.asked || !left.has(a.key));
  const words = previewWords(adds, kept.length);
  const phaseName = (k: string) => phasesOf(model.dto.tree).find((p) => p.key === k)?.name ?? k;
  const late = (list: readonly typeof adds[number][]) => list.filter((a) => a.late).length;
  const lateAdd = late(kept);
  const lateOnly = late(asked);
  const own = model.dto.kind === 'internal';
  const go = async (list: readonly typeof adds[number][], lateCount: number) => {
    setBusy(true);
    setErr(null);
    try {
      // What it needs first, then the steps asked: the order the work runs in.
      const ordered = [...list.filter((a) => !a.asked), ...list.filter((a) => a.asked)].map((a) => a.key);
      await processAction(projectId, '/enable', { keys: ordered, ...(lateCount ? { reason: reason.trim() } : {}) });
      onDone();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const lateField = lateAdd ? (
    <div className="mt-2">
      <input
        className="field w-full max-w-md"
        aria-label={lateAdd === 1 ? 'Why it comes late' : 'Why they come late'}
        placeholder={lateAdd === 1 ? 'Why it comes late' : 'Why they come late'}
        value={reason}
        maxLength={500}
        onChange={(e) => setReason(e.target.value)}
      />
      <p className="meta mt-1">{own ? 'NSB’s own job: the reason is enough.' : 'The client reads the reason and confirms it: until then it is held, and cannot start.'}</p>
    </div>
  ) : null;
  /** A row of the preview: a need, ticked and able to be unticked, or a step ticked, each with where it lands. */
  const rowOf = (a: (typeof adds)[number]) => {
    const name = titleCase(a.title);
    const picture = icons.urls.get(a.key) ?? null;
    const row = (
      <>
        {picture ? <img src={picture} alt="" className="h-8 w-11 shrink-0 rounded-[2px] object-cover" /> : <span className="h-8 w-11 shrink-0 rounded-[2px] bg-inset" aria-hidden="true" />}
        <span className="min-w-0 flex-1">
          {name} <span className="meta">· {phaseName(a.phase)}{a.filed ? ', left out of this job' : ''}</span>
        </span>
        {a.late ? <span className="shrink-0 font-mono text-[0.75rem] text-alert">Late · lands in {phaseName(a.phase)}</span> : null}
      </>
    );
    return (
      <li key={a.key}>
        {a.asked ? (
          <div className="flex min-h-11 items-center gap-3 py-1">{row}</div>
        ) : (
          <label className="flex min-h-11 cursor-pointer items-center gap-3 py-1">
            <input
              type="checkbox"
              className="size-4 shrink-0 accent-ink"
              aria-label={name}
              checked={!left.has(a.key)}
              onChange={(e) =>
                setLeft((s) => {
                  const n = new Set(s);
                  if (e.target.checked) n.delete(a.key);
                  else n.add(a.key);
                  return n;
                })
              }
            />
            {row}
          </label>
        )}
      </li>
    );
  };
  return (
    <div role="group" aria-label={words.heading} className="my-2 ml-0 border border-rule-strong bg-paper px-3 py-2.5 text-[0.9375rem] sm:ml-8">
      <p>{words.heading}</p>
      <ul className="mt-1">
        {(needs.length ? needs : asked).map(rowOf)}
      </ul>
      {/* The steps ticked, each where it lands and late or not, when what they need is listed above them. */}
      {needs.length ? (
        <>
          <p className="meta mt-2">Ticked</p>
          <ul>{asked.map(rowOf)}</ul>
        </>
      ) : null}
      {endComes(model, kept) ? <p className="meta mt-2">Project Complete comes with it, the job’s last step.</p> : null}
      {lateField}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
        <Button busy={busy} disabled={lateAdd > 0 && !reason.trim()} onClick={() => void go(kept, lateAdd)}>
          {words.add}
        </Button>
        {needs.length ? (
          <Button tone="quiet" busy={busy} disabled={lateOnly > 0 && !reason.trim()} onClick={() => void go(asked, lateOnly)}>
            {words.only}
          </Button>
        ) : null}
        <Button tone="quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}
